import type { ChunkAccumulator } from "./chatSliceTypes";

/** Use a Set to prevent processing the same activity ID multiple times. */
export const handledActivityIds = new Set<string>();

/**
 * Generated once on module load so a page refresh (which re-evaluates the
 * module) can be told apart from an in-session reinitialisation.
 */
export const currentSessionId = Math.random().toString(36).substring(7);

/** Track highest status sequence per turn — fire-and-forget causes late arrivals. */
export const highestStatusSequenceByTurn: Map<string, number> = new Map();

/** Phase 3: chunk accumulators for reassembling chunked results payloads. */
export const chunkAccumulatorsByChunkId = new Map<string, ChunkAccumulator>();

/**
 * Mutable listener/connection bookkeeping.
 *
 * These live on an object rather than as `let` bindings because ES module
 * exports are read-only across module boundaries — the thunks that own the
 * DirectLine lifecycle have to be able to write them.
 */
export const chatModuleState = {
  /** Session ID seen the last time the listener was initialised. */
  lastSessionId: null as string | null,
  isListenerInitialized: false,
  activitySubscription: null as { unsubscribe: () => void } | null,
  /** Mutex for DirectLine reconnection — prevents concurrent attempts. */
  reconnectionInProgress: null as Promise<void> | null,
  /** Cancels in-flight conversation loads (rehydration, project fetch, …). */
  conversationLoadAbortController: null as AbortController | null,
};

/**
 * Cancel any ongoing conversation load operations.
 * Call this before starting a new project or switching conversations.
 */
export const cancelOngoingConversationLoad = (): void => {
  if (chatModuleState.conversationLoadAbortController) {
    console.log("🛑 [CANCEL] Aborting ongoing conversation load");
    chatModuleState.conversationLoadAbortController.abort();
    chatModuleState.conversationLoadAbortController = null;
  }
};

/**
 * Create a new AbortController for conversation loading.
 * Automatically cancels any existing controller first.
 */
export const createConversationLoadAbortController = (): AbortController => {
  cancelOngoingConversationLoad();
  chatModuleState.conversationLoadAbortController = new AbortController();
  return chatModuleState.conversationLoadAbortController;
};

/** Drop the activity subscription and forget every per-conversation marker. */
export const teardownActivitySubscription = (): void => {
  if (chatModuleState.activitySubscription) {
    chatModuleState.activitySubscription.unsubscribe();
    chatModuleState.activitySubscription = null;
  }
  chatModuleState.isListenerInitialized = false;
  handledActivityIds.clear();
};

/**
 * Resets module-level state for a new project.
 * This includes the listener initialization flag, activity IDs set, and DirectLine connection.
 * Call this when starting a completely new project to ensure clean state.
 */
export const resetChatModuleState = async (): Promise<void> => {
  console.log("🧹 [RESET] resetChatModuleState called");

  // Unsubscribe from old activity subscription to prevent "conversation ended" errors
  if (chatModuleState.activitySubscription) {
    console.log("🧹 [RESET] Unsubscribing from activity subscription");
  }
  console.log(
    "🧹 [RESET] Resetting listener initialization flag (was:",
    chatModuleState.isListenerInitialized,
    ") and clearing handled activity IDs (count:",
    handledActivityIds.size,
    ")"
  );
  teardownActivitySubscription();

  // Phase 3: Clear all chunk accumulators
  console.log("🧹 [RESET] Clearing chunk accumulators (count:", chunkAccumulatorsByChunkId.size, ")");
  chunkAccumulatorsByChunkId.clear();

  // Clear status sequence tracking for out-of-order handling
  console.log(
    "🧹 [RESET] Clearing status sequence tracking (count:",
    highestStatusSequenceByTurn.size,
    ")"
  );
  highestStatusSequenceByTurn.clear();

  // Reset DirectLine connection - must await to ensure old connection fully closes
  console.log("🧹 [RESET] Resetting DirectLine connection");
  const { resetDirectLine } = await import("../directLlineClient");
  await resetDirectLine();
  console.log("✅ [RESET] resetChatModuleState completed");
};
